import { Injectable } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { Observable } from 'rxjs';
import { FamilyMemberFormService } from 'src/app/_services/family-member-form.service';
import { ProposalService } from 'src/app/_services/proposal.service';
import { APIResponse } from 'src/app/_services/types/api-response.type';
import { QuestionnaireAnswers } from 'src/app/_services/types/building.types';
import {
  BusinessBuildingInsuranceMetadata,
  CreateProposalResponse,
} from 'src/app/_services/types/proposals.type';
import { UserAuthService } from 'src/app/_services/user-auth.service';
import { InsuranceTypeEnum } from 'src/app/enums/insurance-type.enum';

import { BaseBuildingService } from '../../building/base-building.service';
import { BUILDING_INSURANCE_INPUT } from '../../building/constants';
import { BUSINESS_INSURANCE_BASE } from '../company-insurance-constants';
import {
  BUSINESS_BUILDING_FORM_URL,
  BUSINESS_BUILDING_INPUT_STEPS,
  BUSINESS_BUILDING_INSURANCE_BASE,
} from './constants';

@Injectable({
  providedIn: 'root',
})
export abstract class BusinessBuildingService extends BaseBuildingService {
  constructor(
    router: Router,
    proposalService: ProposalService,
    familyMemberService: FamilyMemberFormService,
    route: ActivatedRoute,
    authService: UserAuthService,
  ) {
    super(router, proposalService, familyMemberService, route, authService);
    this.router.navigateByUrl(`${BUSINESS_BUILDING_FORM_URL}/${BUILDING_INSURANCE_INPUT}`, {
      replaceUrl: true,
    });
    this.handleQueryParams();

    this.navigationChangeSubscription = this.router.events.subscribe((val) => {
      if (!(val instanceof NavigationEnd)) return;
      if (
        val.urlAfterRedirects.includes(BUSINESS_INSURANCE_BASE) &&
        val.urlAfterRedirects.includes(BUSINESS_BUILDING_INSURANCE_BASE) &&
        val.urlAfterRedirects.includes(BUSINESS_BUILDING_INPUT_STEPS.INSURANCE_FORM.url)
      )
        this.handleQueryParams();
    });
  }

  sendBuildingInsuranceRequest(
    questionnaire: QuestionnaireAnswers,
  ): Observable<APIResponse<CreateProposalResponse>> {
    const metadata: BusinessBuildingInsuranceMetadata = {
      type: InsuranceTypeEnum.BUSINESS_BUILDING,
      ...this.buildingFormData.toMetadata(),
      pastInsured: questionnaire.hasInsurance,
      companyName: questionnaire.insurance,
      reason: questionnaire.reason,
      anyClaims: questionnaire.hasClaims,
      claimDetails: questionnaire.claims,
    };

    const payload = {
      memberId: this.familyMemberService.mainMemberId,
      insuranceType: InsuranceTypeEnum.BUSINESS_BUILDING,
      metadata,
      originalProposalId: this.buildingFormData.originalProposalId,
      originalLeadId: this.buildingFormData.originalLeadId,
    };
    return this.proposalService.addProposalRequest(payload);
  }

  override handleQueryParams() {
    this.flowReady.next(false);
    if (this.route.snapshot.queryParamMap.get('leadId')) {
      const leadId: string = this.route.snapshot.queryParamMap.get('leadId');

      if (this.authService.isUserLoggedIn()) {
        this.proposalService.getLeadDetails(leadId).subscribe({
          next: (res) => {
            if (!res.data || !res.data.metadata) return;
            if (res.data.insuranceType !== InsuranceTypeEnum.BUSINESS_BUILDING)
              throw Error('Insurance type does not match the flow');
            this.buildingFormData.fromLead(res.data);
            this.flowReady.next(true);
          },
        });
      } else this.flowReady.next(true);
    } else if (this.route.snapshot.queryParamMap.get('proposalId')) {
      const proposalId: string = this.route.snapshot.queryParamMap.get('proposalId');
      if (this.authService.isUserLoggedIn()) {
        this.proposalService.getProposalDetails(proposalId).subscribe({
          next: (res) => {
            if (!res.data) return;
            if (res.data.insuranceType !== InsuranceTypeEnum.BUSINESS_BUILDING)
              throw Error('Insurance type does not match the flow');
            this.buildingFormData.originalProposalId = res.data.proposalId;
            this.fillFromUser().subscribe((v) => {
              if (v) {
                this.flowReady.next(true);
              }
            });
          },
        });
      } else this.flowReady.next(true);
    } else {
      this.fillFromUser().subscribe((v) => {
        if (v) {
          this.flowReady.next(true);
        }
      });
    }
  }
}
